import React, { useEffect } from 'react';
import { Box, Table, TableBody, TableHead } from '@mui/material';
import CardsTableColumnsHead from './components/CardsTableColumnsHead';
import { CardsTableBodyRow, CardsTableProps } from './types';
import CardsTableCell from './components/CardsTableCell';
import CardsTableEmptyPlaceholder from './components/CardsTableEmptyPlaceholder';
import CardsTableLoadingIndicator from './components/CardsTableLoadingIndicator';
import CardsTableRow from './components/CardsTableRow';

export const CardsTable = (props: CardsTableProps) => {
  const isColorMix = props.rowDividerType === 'color-mix';
  const rows = props.cellsData.bodyRows;

  // Handle infinite scroll
  const { onEndReached } = props;
  useEffect(() => {
    if (!onEndReached) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      
      // Trigger when user scrolls to within 200px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        if (onEndReached) {
          onEndReached();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onEndReached]);

  const renderRowContent = (bodyRow: CardsTableBodyRow) => {
    return bodyRow.components.map((component, cellIndex) => (
      <CardsTableCell
        key={bodyRow.key + props.cellsData.headCells[cellIndex].key}
        id={bodyRow.key + props.cellsData.headCells[cellIndex].key}
        isColorMix={isColorMix}
        cellWidth={props.cellsData.headCells[cellIndex].columnWidth}
      >
        {component}
      </CardsTableCell>
    ));
  };

  return (
    <Box
      sx={{
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        position: 'relative',
        width: '100%',
        '@media (max-width: 768px)': {
          borderRadius: '8px',
        },
      }}
    >
      <Table
        key={`table-cols-${props.cellsData.headCells.length}`}
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '@media (max-width: 768px)': {
            fontSize: '12px',
          },
          '@media (max-width: 480px)': {
            fontSize: '10px',
          },
        }}
      >
        <TableHead>
          <CardsTableColumnsHead {...props} />
        </TableHead>
        <TableBody>
          {rows.map((bodyRow, index) => (
            <CardsTableRow
              key={bodyRow.key}
              index={index}
              isColorMix={isColorMix}
              isSelected={props.selectedCard?.id === bodyRow.card.id}
              className={bodyRow.className}
              onClick={() => {
                // Use row's onClick if provided, otherwise use default card select
                if (bodyRow.onClick) {
                  bodyRow.onClick();
                } else if (props.onCardSelect) {
                  props.onCardSelect(bodyRow.card);
                }
              }}
              onMouseEnter={bodyRow.onMouseEnter}
              onMouseLeave={bodyRow.onMouseLeave}
            >
              {renderRowContent(bodyRow)}
            </CardsTableRow>
          ))}
        </TableBody>
      </Table>

      {!props.cellsData.bodyRows.length && !props.isLoading && (
        <CardsTableEmptyPlaceholder onImportClick={props.onImportClick} />
      )}
      {props.isLoading && <CardsTableLoadingIndicator />}
    </Box>
  );
};
